import React, { useCallback, useMemo, useState } from "react"
import { DateTime } from "luxon"
import { Box, Grid, MenuItem, Stack } from "@mui/material"
import { UseQueryResult } from "@tanstack/react-query"
import { MRT_ColumnDef, MRT_FullScreenToggleButton, MRT_Row } from "material-react-table"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
// import { SelectAdyenAccountAutocomplete } from "../components/SelectAdyenAccountAutocomplete"
import { SelectMerchantAutocomplete } from "../components/SelectMerchantAutocomplete"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { SUPPLIER_ADMIN_ROUTE } from "../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import {
    fetchSupplierAcquiringBankTransfers,
    SupplierAcquiringReportsQuery,
    SupplierAcquiringBankTransfersResponse,
} from "../supplierAcquiringReportsApi"
import { useApiQuery } from "../../../../../core/api/useApiQuery"

import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../shared/components/table/TableSummaryBox"
import { TransferItem } from "../supplierAcquiringReportsTypes"
import { useSupplier } from "../../../supplierHooks"
import { filterDataByMerchantAndAdyentId } from "../reconciliationLib"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { useExportAcquiringReportsBankTransfersToCsv } from "../hooks/useExportAcquiringReportsBankTransfersToCsv"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { makeStyles } from "tss-react/mui"
import { InfoBox } from "../../../../merchant/admin/shops/components/formElements/InfoBox"
import { StandardToggleGlobalFilterButton } from "../../../../../shared/components/table/StandardToggleGlobalFilterButton"
import { TransferDetailsDialog } from "../components/transferDetails/TransferDetailsDialog"
import { useDateTime } from "../../../../localization/useDateTime"
import { findLast } from "lodash-es"
import { type DtDateRange, StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"

const useStyles = makeStyles()(() => ({
    infoWrapper: {
        padding: "1rem",
        width: "fit-content",
    },
}))

const selectBankTransfers = (data: SupplierAcquiringBankTransfersResponse) => data?.transfers || []

type DetailPeriod = Readonly<{
    currentRowBookingDate: string | undefined
    previousRowBookingDate: string | undefined
}>

type FilterState = Readonly<{
    merchantId?: string
    adyentId?: string
}>

export function BankTransfers() {
    const { classes } = useStyles()
    const { supplierId } = useSupplier()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()

    const [showDetailsDialog, setShowDetailsDialog] = useState(false)

    const [selectedBookingDates, setSelectedBookingDates] = useState<DetailPeriod>({
        currentRowBookingDate: undefined,
        previousRowBookingDate: undefined,
    })

    const maxDate = DateTime.now().minus({ days: 1 })
    const { startDate, endDate } = useInitialDateFilters(maxDate, maxDate)
    const [dateState, setDateState] = useState<DtDateRange>({
        startDate: startDate,
        endDate: endDate,
    })

    const [filters, setFilters] = useState<FilterState>({
        merchantId: undefined,
        adyentId: undefined,
    })

    const bankTransfers = useApiQuery(fetchSupplierAcquiringBankTransfers, {
        queryName: SupplierAcquiringReportsQuery.BANK_TRANSFERS_LIST,
        dependencies: {
            supplierId,
            includePrevBankTransfer: true,
            fromDate: dateState.startDate.toISODate(),
            toDate: dateState.endDate.toISODate(),
        },
        select: selectBankTransfers,
    })

    const tableData = useMemo(() => {
        const data = filterDataByMerchantAndAdyentId(bankTransfers.data, filters)

        return data.filter((item) => {
            const bookingDateRaw = dateTimeFactory.fromApi(item.bookingDate)
            const bookingDate = bookingDateRaw.startOf("day")
            const start = dateState.startDate.startOf("day")
            const end = dateState.endDate.endOf("day")

            return bookingDate >= start && bookingDate <= end
        })
    }, [bankTransfers.data, filters, dateState.startDate])

    const { ApiQueryTable, columnFactory } = useApiQueryTable({ ...bankTransfers, data: tableData } as UseQueryResult<
        typeof tableData
    >)

    const columns = useMemo<MRT_ColumnDef<TransferItem>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("supplier-acquiring-bank-transfers-table-transfer-id-column"),
                accessorKey: "transferId",
                muiTableHeadCellProps: {
                    align: "center",
                },
                muiTableBodyCellProps: {
                    align: "center",
                    style: {
                        verticalAlign: "baseline",
                    },
                },
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("supplier-acquiring-reports-table-booked-time-column"),
                    accessorKey: "bookingDate",
                },
                (row) => row.bookingDate,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("supplier-acquiring-bank-transfers-table-available-time-column"),
                    accessorKey: "valueDate",
                },
                (row) => row.valueDate,
            ),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-acquiring-reports-table-merchant-column"),
                accessorKey: "merchant",
                Cell: ({ row }) => {
                    return (
                        <>
                            {row.original.merchant ? (
                                <>
                                    {row.original.merchant.caseNumber ? `${row.original.merchant.caseNumber}, ` : ""}
                                    {row.original.merchant.name}
                                </>
                            ) : null}
                        </>
                    )
                },
            }),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-acquiring-bank-transfers-table-bank-account-column"),
                accessorKey: "bankAccount.accountIdentifier",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("supplier-acquiring-reports-table-amount-column"),
                },
                (row) => row.amount,
                true,
            ),
        ],
        [columnFactory, l10n],
    )

    const renderRowActionMenuItems = useCallback(
        ({ row, closeMenu }: { row: MRT_Row<TransferItem>; closeMenu: () => void }) => [
            <MenuItem
                key={`details-${row.original.transferId}`}
                onClick={() => {
                    const currentRowBookingDate = row.original.bookingDate
                    const currentBookingDateTime = dateTimeFactory.fromApi(currentRowBookingDate)

                    // NOTE: findLast can handle the collection being undefined
                    const previousRow = findLast(
                        bankTransfers?.data,
                        (item) => dateTimeFactory.fromApi(item.bookingDate) < currentBookingDateTime,
                    )

                    const previousBookingDate = previousRow?.bookingDate

                    setSelectedBookingDates({
                        currentRowBookingDate,
                        previousRowBookingDate: previousBookingDate || undefined,
                    })

                    setShowDetailsDialog(true)
                    closeMenu()
                }}
            >
                <LocalizedStrict id="supplier-acquiring-bank-transfers-table-details-menu-item">
                    <span>Details</span>
                </LocalizedStrict>
            </MenuItem>,
        ],
        [dateTimeFactory, bankTransfers?.data, setSelectedBookingDates, setShowDetailsDialog],
    )

    const { handleExportRows } = useExportAcquiringReportsBankTransfersToCsv(
        dateState.startDate?.toISODate() ?? "",
        dateState.endDate?.toISODate() ?? "",
    )

    return (
        <>
            <LocalizedStrict id="supplier-acquiring-bank-transfers-header" attrs={{ title: true }}>
                <PageHeader page={Page.SUPPLIER_ADMIN_ACQUIRING_BANK_TRANSFERS} title="Bank transfers">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-acquiring-reports">
                            <Breadcrumb>Reconciliation</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-acquiring-bank-transfers">
                            <Breadcrumb>Bank transfers</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{
                        density: "spacious",
                        sorting: [
                            {
                                id: "bookingDate",
                                desc: false,
                            },
                        ],
                    }}
                    renderRowActionMenuItems={renderRowActionMenuItems}
                    renderTopToolbar={({ table }) => (
                        <Box>
                            <Box className={classes.infoWrapper}>
                                <LocalizedStrict
                                    id="supplier-acquiring-bank-transfers-table-info-banner"
                                    attrs={{ description: true }}
                                >
                                    <InfoBox description="Data for this report becomes available on the next day, so you cannot see transfers for today or future transfers." />
                                </LocalizedStrict>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <StandardTableFilter variant="left">
                                    <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                                    <SelectMerchantAutocomplete
                                        merchantId={filters.merchantId}
                                        setMerchantId={(merchantId) =>
                                            setFilters((prev) => ({
                                                ...prev,
                                                merchantId,
                                            }))
                                        }
                                    />
                                    {/*<SelectAdyenAccountAutocomplete*/}
                                    {/*    adyentId={filtersList.adyentId}*/}
                                    {/*    setAdyentId={(adyentId) =>*/}
                                    {/*        setFiltersList((prev) => ({*/}
                                    {/*            ...prev,*/}
                                    {/*            adyentId,*/}
                                    {/*        }))*/}
                                    {/*    }*/}
                                    {/*/>*/}
                                </StandardTableFilter>
                                <Grid container flexWrap="nowrap" justifyContent="end" gap={2} padding="1rem">
                                    <Stack direction="row" spacing={1}>
                                        <StandardExportButton
                                            onClick={() => {
                                                handleExportRows(tableData)
                                            }}
                                        />
                                        <StandardToggleGlobalFilterButton table={table} />
                                        <MRT_FullScreenToggleButton table={table} />
                                    </Stack>
                                </Grid>
                            </Box>
                        </Box>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap>
                            <LocalizedStrict
                                id="supplier-acquiring-bank-transfers-table-number-of-transfers"
                                attrs={{ title: true }}
                            >
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    title="Number of transfers"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                />
            </Grid>

            {showDetailsDialog && (
                <TransferDetailsDialog
                    open={showDetailsDialog}
                    onClose={() => setShowDetailsDialog(false)}
                    fromDateTime={selectedBookingDates.previousRowBookingDate}
                    toDateTime={selectedBookingDates.currentRowBookingDate}
                />
            )}
        </>
    )
}
